/**
 * Shared Basecamp due-date badge (Todos table + list viewers + previews).
 * Prefer this over raw `text-red-*` on dark backgrounds.
 */

import { Badge } from '@/components/ui/badge'
import {
  isBasecampDueOverdue as isDueOverdue,
  isBasecampDueToday as isDueToday,
  todayInLosAngeles,
} from '@/lib/basecamp-calendar'
import { cn, parseLocalDate } from '@/lib/utils'

export function formatBasecampOverdueLabel(
  dueOn: string | null | undefined,
): string {
  if (!dueOn) return ''
  const today = todayInLosAngeles()
  if (dueOn > today) return ''
  const due = parseLocalDate(dueOn)
  const todayStart = parseLocalDate(today)
  const daysOverdue = Math.max(
    0,
    Math.floor((todayStart.getTime() - due.getTime()) / 86400000),
  )
  if (daysOverdue === 0) return 'Due today'
  if (daysOverdue < 7) return `${daysOverdue}d overdue`
  if (daysOverdue < 28) return `${Math.floor(daysOverdue / 7)}w overdue`
  if (daysOverdue < 365) return `${Math.floor(daysOverdue / 30)}mo overdue`
  return `${Math.floor(daysOverdue / 365)}y overdue`
}

export function formatBasecampDueAbsolute(
  dueOn: string | null | undefined,
): string {
  if (!dueOn) return ''
  return parseLocalDate(dueOn).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatBasecampDueShort(
  dueOn: string | null | undefined,
): string {
  if (!dueOn) return ''
  const due = parseLocalDate(dueOn)
  const todayStart = parseLocalDate(todayInLosAngeles())
  const diff = Math.round(
    (due.getTime() - todayStart.getTime()) / 86400000,
  )
  if (diff < 0) return formatBasecampOverdueLabel(dueOn)
  if (diff === 0) return 'Due today'
  if (diff === 1) return 'Tomorrow'
  if (diff <= 7) return `In ${diff}d`
  return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function isBasecampDueToday(
  dueOn: string | null | undefined,
): boolean {
  return isDueToday(dueOn)
}

export function isBasecampOverdue(
  dueOn: string | null | undefined,
  explicit?: boolean | null,
): boolean {
  if (dueOn) return isDueOverdue(dueOn)
  if (explicit != null) return explicit
  return false
}

/** Destructive overdue wash that stays readable on dark surfaces. */
export const BASECAMP_OVERDUE_BADGE_CLASS =
  'border border-red-500/45 bg-red-500/15 text-red-700 dark:border-red-400/50 dark:bg-red-500/20 dark:text-red-200'

export const BASECAMP_DUE_TODAY_BADGE_CLASS =
  'border border-amber-400/50 bg-amber-500/15 text-amber-800 dark:text-amber-200'

/** Row / editor face: tight pill next to a date string. */
export const BASECAMP_DUE_BADGE_COMPACT_CLASS = 'px-1.5 py-0 whitespace-nowrap'

export interface BasecampDueBadgeProps {
  dueOn?: string | null
  /** Prefer assignment._is_overdue when present. */
  isOverdue?: boolean | null
  className?: string
  /** When true, show nothing if there is no due date. Default true. */
  hideIfEmpty?: boolean
  /** Preview / edit trigger: calendar date, plus relative when overdue. */
  showAbsolute?: boolean
  /** Status pill only (Overdue / Due today) - date lives beside the badge. */
  statusOnly?: boolean
}

/**
 * Compact due / overdue pill used across Basecamp list + table surfaces.
 */
export function BasecampDueBadge({
  dueOn,
  isOverdue: isOverdueProp,
  className,
  hideIfEmpty = true,
  showAbsolute = false,
  statusOnly = false,
}: BasecampDueBadgeProps) {
  const overdue = isBasecampOverdue(dueOn, isOverdueProp)
  const dueToday = !overdue && isBasecampDueToday(dueOn)
  const overdueLabel = formatBasecampOverdueLabel(dueOn)
  const dueLabel = formatBasecampDueShort(dueOn)
  const absolute = formatBasecampDueAbsolute(dueOn)
  const label = statusOnly
    ? overdue
      ? 'Overdue'
      : dueToday
        ? 'Due today'
        : ''
    : showAbsolute
      ? overdue && overdueLabel && overdueLabel !== 'Due today'
        ? `${absolute} · ${overdueLabel}`
        : absolute
      : overdue
        ? overdueLabel || dueLabel
        : dueLabel
  if (!label) {
    if (hideIfEmpty) return null
    return (
      <span className={cn('text-[10px] text-muted-foreground', className)}>
        No date
      </span>
    )
  }

  return (
    <Badge
      variant="outline"
      className={cn(
        'text-xs font-medium leading-none',
        overdue && BASECAMP_OVERDUE_BADGE_CLASS,
        !overdue && dueToday && BASECAMP_DUE_TODAY_BADGE_CLASS,
        !overdue && !dueToday && 'text-muted-foreground',
        className,
      )}
    >
      {label}
    </Badge>
  )
}
